import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import Popper from '@material-ui/core/Popper';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import useCenterlineAiStyles from './styles';
import { CITATION_CARD_NOTE, citationFields } from './core/citations';
import { CitationInfo } from './core/types';
import { CopyGlyph } from './Glyphs';

/**
 * The details card for a citation with no CENTERLINE page to link to (chat-ui-spec §6.3). One card
 * at a time; it toggles per marker, and closes on an outside click, Escape, a window resize or a
 * scroll of the thread.
 */

export interface OpenCard {
  anchor: HTMLElement;
  n: number;
  info: Partial<CitationInfo>;
}

interface CardControls {
  toggle(anchor: HTMLElement, n: number, info: Partial<CitationInfo>): void;
  openFor: HTMLElement | null;
}

export const CitationCardContext = createContext<CardControls>({
  toggle: () => undefined,
  openFor: null,
});

export const useCitationCard = () => useContext(CitationCardContext);

/** Marks the elements whose clicks the card's outside-click handler leaves to the marker. */
export const CITE_MARKER_ATTR = 'data-cb-cite';

/** `2795b3a4-…-d200fb`: enough to recognise an id; the button copies all of it. */
const shortId = (id: string) => (id.length > 20 ? `${id.slice(0, 8)}-…-${id.slice(-6)}` : id);

function IdButton({ value }: { value: string }) {
  const classes = useCenterlineAiStyles();
  const [copied, setCopied] = useState(false);
  const timer = useRef<number>();
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const copy = () => {
    Promise.resolve(navigator.clipboard?.writeText(value))
      .then(() => {
        setCopied(true);
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setCopied(false), 900);
      })
      .catch(() => undefined);
  };
  return (
    <button type="button" className={classes.idButton} title="Copy" onClick={copy}>
      {copied ? 'copied' : shortId(value)}
      <CopyGlyph size={12} />
    </button>
  );
}

export default function CitationCard(props: { card: OpenCard | null; onClose(): void }) {
  const { card, onClose } = props;
  const classes = useCenterlineAiStyles();

  useEffect(() => {
    if (!card) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onClose);
    };
  }, [card, onClose]);

  if (!card) return null;

  return (
    <Popper
      open
      anchorEl={card.anchor}
      placement="bottom-start"
      className={classes.citeCard}
      modifiers={{ offset: { offset: '0,6' }, preventOverflow: { padding: 8 }, flip: {} }}
    >
      <ClickAwayListener
        onClickAway={(e) => {
          const target = e.target as HTMLElement | null;
          if (target?.closest?.(`[${CITE_MARKER_ATTR}]`)) return;
          onClose();
        }}
      >
        <div className={classes.citeCardBody} role="dialog" aria-label={`Citation ${card.n}`}>
          <div className={classes.citeCardHead}>Citation [{card.n}]</div>
          <dl className={classes.citeFields}>
            {citationFields(card.info).map((f) => (
              <React.Fragment key={f.label}>
                <dt>{f.label}</dt>
                <dd>{f.isId ? <IdButton value={f.value} /> : f.value}</dd>
              </React.Fragment>
            ))}
          </dl>
          <p className={classes.citeNote}>{CITATION_CARD_NOTE}</p>
        </div>
      </ClickAwayListener>
    </Popper>
  );
}
